import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Users, Search, ChevronLeft, ChevronRight, Receipt, RefreshCw, Building2, Eye, Trash2, ArrowLeft } from 'lucide-react';
import { adminApiClient } from '../lib/api';

interface LegacyDevotee {
  _id: string;
  donorId?: number;
  name?: string;
  donorFullName?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  city?: string;
  state?: string;
  country?: string;
  gothraDetails?: string;
  nakshatram?: string;
  status?: string;
}

interface LegacyDevoteesPageProps {
  onViewTransactions?: (devotee: LegacyDevotee) => void;
}

const LegacyDevoteesPage: React.FC<LegacyDevoteesPageProps> = ({ onViewTransactions }) => {
  const [temples, setTemples] = useState<any[]>([]);
  const [selectedTemple, setSelectedTemple] = useState<any | null>(null);
  const [items, setItems] = useState<LegacyDevotee[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingTemples, setLoadingTemples] = useState(true);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const limit = 50;

  const totalPages = Math.ceil(total / limit);

  // Use a userRole state to know if we're super_admin to skip or show temples maybe?
  useEffect(() => {
    (async () => {
      try {
        const res = await adminApiClient.getTemples({ page: 1, limit: 200 });
        if (res.success && res.data?.temples && res.data.temples.length > 0) {
          setTemples(res.data.temples);
          // If only 1 temple (e.g. temple admin somehow), just select it to bypass the temple list
          if (res.data.temples.length === 1) {
            setSelectedTemple(res.data.temples[0]);
          }
        }
      } catch (e: any) {
        // failed to get temples, we might not be super admin
      } finally {
        setLoadingTemples(false);
      }
    })();
  }, []);

  const fetchDevotees = useCallback(async (currentPage: number, search: string, templeId?: string) => {
    setLoading(true);
    try {
      const res = await adminApiClient.getLegacyDevotees({ page: currentPage, limit, q: search, templeId });
      if (res.success) {
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      } else {
        toast.error(res.message || 'Failed to load legacy devotees');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to load legacy devotees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If a temple is selected, or if temples were loaded but we have none (meaning we aren't a super admin that can list temples)
    if (selectedTemple || temples.length === 0) {
      if (!loadingTemples) {
        fetchDevotees(page, q, selectedTemple?._id);
      }
    }
  }, [page, selectedTemple, loadingTemples]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchDevotees(1, q, selectedTemple?._id);
  };

  const handleDelete = async (devoteeId: string) => {
    if (!window.confirm('Are you sure you want to delete this legacy devotee? This will also delete their transactions.')) return;
    try {
      const res = await adminApiClient.deleteLegacyDevotee(devoteeId);
      if (res.success) {
        toast.success(res.message);
        fetchDevotees(page, q, selectedTemple?._id);
      } else {
        toast.error(res.message || 'Failed to delete legacy devotee');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to delete legacy devotee');
    }
  };

  const displayName = (d: LegacyDevotee) =>
    d.name || d.donorFullName || d.fullName || '—';

  if (loadingTemples) {
    return <div className="p-6 text-gray-500">Loading...</div>;
  }

  // If temples exist and none is selected, show temple list
  if (temples.length > 1 && !selectedTemple) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[var(--color-primary)]" />
            Select Temple
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Choose a temple to view its legacy devotees
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {temples.map(t => (
            <div key={t._id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 hover:border-[var(--color-primary)] transition-colors cursor-pointer flex items-center justify-between" onClick={() => { setSelectedTemple(t); setPage(1); setQ(''); }}>
              <div>
                <h3 className="font-bold text-gray-900">{t.name}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{t.address?.city || 'No city'}, {t.address?.state || 'No state'}</p>
              </div>
              <button className="p-2 text-[var(--color-primary)] bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] rounded-lg">
                <Eye className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          {temples.length > 1 && selectedTemple && (
            <button onClick={() => { setSelectedTemple(null); setTotal(0); setItems([]); }} className="mb-3 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
              <ArrowLeft className="w-4 h-4" /> Back to Temples
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-[var(--color-primary)]" />
            Legacy Devotees {selectedTemple ? `— ${selectedTemple.name}` : ''}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Historical donors imported from legacy data — {total} records
          </p>
        </div>
        <button
          onClick={() => fetchDevotees(page, q, selectedTemple?._id)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium text-gray-600"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, or phone…"
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_30%,white)] focus:border-[var(--color-primary)] outline-none"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold text-sm bg-[linear-gradient(90deg,var(--color-primary),var(--color-tertiary))] hover:opacity-90"
        >
          <Search className="w-4 h-4" /> Search
        </button>
      </form>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Name</th>
                <th className="text-left px-5 py-3 font-semibold">Email</th>
                <th className="text-left px-5 py-3 font-semibold">Phone</th>
                <th className="text-left px-5 py-3 font-semibold">Location</th>
                <th className="text-left px-5 py-3 font-semibold">Gothra</th>
                <th className="text-left px-5 py-3 font-semibold">Nakshatra</th>
                <th className="text-right px-5 py-3 font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-gray-400">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-gray-400">
                    No legacy devotees found. Import data to see records here.
                  </td>
                </tr>
              ) : (
                items.map((d) => (
                  <tr key={d._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 font-medium text-gray-900">{displayName(d)}</td>
                    <td className="px-5 py-3 text-gray-600">{d.email || '—'}</td>
                    <td className="px-5 py-3 text-gray-600">{d.phone || '—'}</td>
                    <td className="px-5 py-3 text-gray-600">
                      {[d.city, d.state, d.country].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="px-5 py-3 text-gray-600">{d.gothraDetails || '—'}</td>
                    <td className="px-5 py-3 text-gray-600">{d.nakshatram || '—'}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {d.donorId != null && (
                          <button
                            onClick={() => onViewTransactions?.(d)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--color-primary)] text-[var(--color-primary)] text-xs font-semibold hover:bg-[color-mix(in_srgb,var(--color-primary)_8%,white)] transition-colors"
                          >
                            <Receipt className="w-3.5 h-3.5" /> <span className="hidden xl:inline">Transactions</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(d._id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 transition-colors"
                          title="Delete Legacy Devotee"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages} — {total} total
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded border border-gray-200 hover:bg-white disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded border border-gray-200 hover:bg-white disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LegacyDevoteesPage;
